import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid2';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { useAuthStore } from '@/store/auth';
import { useEntityStore } from '@/store/entity';
import { useEntities } from '@/api/entities';
import { useMoneyFmt } from '@/lib/money';
import { useDashboardSummary } from '@/api/dashboard';
import { APP_BASE } from '@/lib/api-base';
import { HrmsSyncCard } from '@/components/hrms-sync/HrmsSyncCard';
import type { SvgIconComponent } from '@mui/icons-material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PeopleIcon from '@mui/icons-material/People';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import BarChartIcon from '@mui/icons-material/BarChart';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Admin dashboard — the landing page for staff logins. Driven by
 * GET /api/dashboard/summary (entity-scoped via the topbar selector).
 *
 * Surfaces the four things an operator checks first thing in the day:
 *   1. Headcount      — active vs total
 *   2. Latest run     — period, status, net payout
 *   3. Approvals      — what's waiting on a human (reimb / IT-decl / CTC / arrears)
 *   4. Compliance     — pending statutory challans
 * plus quick-links into the high-traffic modules.
 */

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  CALCULATED: 'info',
  APPROVED: 'success',
  LOCKED: 'success',
  PAID: 'success',
};

/** Accent palette for the KPI icon badges — soft tint ground + solid glyph. */
type Accent = 'blue' | 'violet' | 'amber' | 'teal';
const ACCENT: Record<Accent, { bg: string; fg: string }> = {
  blue:   { bg: '#e8f4fd', fg: '#108ae6' },
  violet: { bg: '#eeeafc', fg: '#6d54d6' },
  amber:  { bg: '#fdf2d9', fg: '#c8891a' },
  teal:   { bg: '#dcf5ef', fg: '#12987a' },
};

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const entityId = useEntityStore((s) => s.entityId);
  // Entity config-driven money formatter (respects rounding_precision /
  // number_format / currency_symbol from Payroll Config → Rounding).
  const inr = useMoneyFmt(entityId);
  const { data: entities } = useEntities();
  const activeEntity = entities?.find((e) => e.id === entityId);
  const { data, isLoading, error } = useDashboardSummary(entityId);

  const approvalsHot = (data?.approvals.total ?? 0) > 0;
  // Starting a sync or confirming a mapping is for admins and HR; other
  // staff can still see the sync state and its history.
  const canSync = user?.role === 'ADMIN' || user?.role === 'HR_MANAGER';
  const challansHot = (data?.compliance.challans_pending ?? 0) > 0;
  const returnsLate = data?.compliance.returns_overdue ?? 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="h1">
          Welcome back{user?.full_name ? `, ${user.full_name}` : ''}
        </Typography>
        {data?.fiscal_year && (
          <Chip size="small" variant="outlined" label={`FY ${data.fiscal_year}`} />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {activeEntity
          ? `Showing figures for ${activeEntity.name} (${activeEntity.short_code}).`
          : 'Showing figures across all entities — pick one from the top bar to scope.'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          Couldn’t load the dashboard summary. {String((error as Error)?.message ?? error)}
        </Alert>
      )}

      {/* Needs-attention banner — a clean clickable strip, not the default
          MUI warning alert. */}
      {!isLoading && data && (approvalsHot || challansHot || returnsLate > 0) && (
        <Box
          component={RouterLink}
          to={`${APP_BASE}${approvalsHot ? '/approvals' : '/statutory-returns'}`}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5,
            p: '12px 16px', borderRadius: 2, textDecoration: 'none', color: 'inherit',
            bgcolor: '#fdf6e7', border: '1px solid #f2dca6',
            transition: 'box-shadow 140ms, transform 140ms',
            '&:hover': { boxShadow: '0 4px 14px rgba(200,137,26,0.16)', transform: 'translateY(-1px)' },
            '&:hover .attn-cta': { gap: 0.75 },
          }}
        >
          <Box sx={{
            flexShrink: 0, width: 34, height: 34, borderRadius: '9px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: '#fbecc8', color: '#c8891a',
          }}>
            <WarningAmberIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, fontSize: 13.5, color: '#7a5a12' }}>
            {approvalsHot && (
              <>
                <b>{data.approvals.total}</b> item{data.approvals.total === 1 ? '' : 's'} awaiting action
              </>
            )}
            {approvalsHot && challansHot && ' · '}
            {challansHot && (
              <>
                <b>{data.compliance.challans_pending}</b> statutory challan
                {data.compliance.challans_pending === 1 ? '' : 's'} pending deposit
              </>
            )}
            {(approvalsHot || challansHot) && returnsLate > 0 && ' · '}
            {returnsLate > 0 && (
              <>
                <b>{returnsLate}</b> TDS statement{returnsLate === 1 ? '' : 's'} past the filing due date
              </>
            )}
          </Box>
          <Box className="attn-cta" sx={{
            display: 'flex', alignItems: 'center', gap: 0.25,
            flexShrink: 0, fontSize: 13, fontWeight: 700, color: '#b9791a',
            transition: 'gap 140ms',
          }}>
            Review <ChevronRightIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      )}

      {/* A workspace created by self-service signup has no uKnowva to sync from. */}
      {user?.workspace_origin !== 'SIGNUP' && <HrmsSyncCard canEdit={canSync} />}

      {/* KPI cards */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Headcount" to="/employees" icon={PeopleIcon} accent="blue">
            {isLoading ? (
              <Skeleton width={80} height={40} />
            ) : (
              <>
                <Big>{data?.headcount.active ?? 0}</Big>
                <Sub>active · {data?.headcount.total ?? 0} total</Sub>
              </>
            )}
          </StatCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Latest payroll run" to="/payroll-runs" icon={RocketLaunchIcon} accent="violet">
            {isLoading ? (
              <Skeleton width={120} height={40} />
            ) : data?.latest_run ? (
              <>
                <Stack direction="row" useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  <Big sx={{ fontSize: 22 }}>{data.latest_run.period_label}</Big>
                  <Chip
                    size="small"
                    color={STATUS_COLOR[data.latest_run.status] ?? 'default'}
                    label={data.latest_run.status}
                    sx={{ fontWeight: 700, letterSpacing: '0.02em' }}
                  />
                </Stack>
                <Sub>
                  net {inr(data.latest_run.total_net)} · {data.latest_run.employee_count} emp
                </Sub>
              </>
            ) : (
              <>
                <Big sx={{ fontSize: 22, color: 'text.disabled' }}>—</Big>
                <Sub>No runs yet</Sub>
              </>
            )}
          </StatCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Awaiting action" to="/approvals" icon={PendingActionsIcon}
            accent="amber" highlight={approvalsHot}>
            {isLoading ? (
              <Skeleton width={60} height={40} />
            ) : (
              <>
                <Big sx={approvalsHot ? { color: ACCENT.amber.fg } : undefined}>
                  {data?.approvals.total ?? 0}
                </Big>
                <Sub>approvals pending</Sub>
              </>
            )}
          </StatCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Challans pending" to="/challan/entries" icon={AccountBalanceIcon}
            accent="teal" highlight={challansHot}>
            {isLoading ? (
              <Skeleton width={60} height={40} />
            ) : (
              <>
                <Big sx={challansHot ? { color: ACCENT.teal.fg } : undefined}>
                  {data?.compliance.challans_pending ?? 0}
                </Big>
                <Sub>
                  TDS deposits due
                  {data?.compliance.challans_overdue ? ` · ${data.compliance.challans_overdue} overdue` : ''}
                  {returnsLate ? ` · ${returnsLate} statement${returnsLate === 1 ? '' : 's'} overdue` : ''}
                </Sub>
              </>
            )}
          </StatCard>
        </Grid>
      </Grid>

      {/* Approvals breakdown + quick links */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
            <Typography variant="h3" sx={{ mb: 2 }}>What needs attention</Typography>
            <Stack spacing={0}>
              <AttnRow label="Reimbursements — approve / reject" to="/reimbursements" icon={ReceiptIcon}
                n={data?.approvals.reimbursements_pending} loading={isLoading} divider />
              <AttnRow label="Reimbursements — awaiting payout" to="/reimbursements" icon={PaymentsIcon}
                n={data?.approvals.reimbursements_awaiting_payout} loading={isLoading} divider />
              <AttnRow label="IT declarations — review" to="/it-declarations" icon={DescriptionIcon}
                n={data?.approvals.it_declarations_pending} loading={isLoading} divider />
              <AttnRow label="CTC revisions — approve" to="/employees" icon={TrendingUpIcon}
                n={data?.approvals.ctc_revisions_pending} loading={isLoading} divider />
              <AttnRow label="Arrears — apply to a run" to="/arrears" icon={BarChartIcon}
                n={data?.approvals.arrears_computed} loading={isLoading} />
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
            <Typography variant="h3" sx={{ mb: 2 }}>Quick links</Typography>
            <Grid container spacing={1.5}>
              {QUICK_LINKS.map((q) => (
                <Grid size={{ xs: 6, sm: 4 }} key={q.to}>
                  <QuickTile {...q} />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Quick-link config ─────────────────────────────────────────────────
const QUICK_LINKS: { to: string; label: string; ic: SvgIconComponent }[] = [
  { to: '/payroll-runs', label: 'Payroll Runs', ic: RocketLaunchIcon },
  { to: '/employees', label: 'Employees', ic: PeopleIcon },
  { to: '/attendance', label: 'Attendance', ic: EventAvailableIcon },
  { to: '/reimbursements', label: 'Reimbursements', ic: ReceiptIcon },
  { to: '/it-declarations', label: 'IT Declarations', ic: DescriptionIcon },
  { to: '/reports', label: 'Reports', ic: BarChartIcon },
];

// ── Presentational helpers ──────────────────────────────────────────────
function StatCard({
  title, to, highlight, accent = 'blue', icon: Ico, children,
}: {
  title: string; to: string; highlight?: boolean; accent?: Accent;
  icon?: SvgIconComponent; children: React.ReactNode;
}) {
  const a = ACCENT[accent];
  return (
    <Paper
      component={RouterLink}
      to={`${APP_BASE}${to}`}
      sx={{
        p: 2.5, border: 1, display: 'block', textDecoration: 'none', color: 'inherit',
        height: '100%', minHeight: 116,
        borderColor: highlight ? 'warning.main' : 'divider',
        position: 'relative', overflow: 'hidden',
        transition: 'box-shadow 140ms, transform 140ms, border-color 140ms',
        '&:hover': { boxShadow: 4, transform: 'translateY(-2px)', borderColor: a.fg },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.06em' }}>
            {title}
          </Typography>
          <Box sx={{ mt: 0.75 }}>{children}</Box>
        </Box>
        {Ico && (
          <Box sx={{
            flexShrink: 0,
            width: 40, height: 40, borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: a.bg,
          }}>
            <Ico sx={{ fontSize: 22, color: a.fg }} />
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function Big({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return <Box sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15, color: '#2f3a45', ...(sx || {}) }}>{children}</Box>;
}
function Sub({ children }: { children: React.ReactNode }) {
  return <Box sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>{children}</Box>;
}

function AttnRow({
  label, to, n, loading, icon: Ico, divider,
}: { label: string; to: string; n?: number; loading: boolean; icon?: SvgIconComponent; divider?: boolean }) {
  const count = n ?? 0;
  const hot = count > 0;
  return (
    <Box
      component={RouterLink}
      to={`${APP_BASE}${to}`}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        py: 1.25, px: 1, textDecoration: 'none', color: 'inherit',
        borderBottom: divider ? '1px solid' : 'none', borderColor: 'divider',
        transition: 'background 120ms',
        '&:hover': { bgcolor: 'action.hover' },
        '&:hover .attn-chevron': { opacity: 1, transform: 'translateX(0)' },
      }}
    >
      {Ico && (
        <Box sx={{
          flexShrink: 0, width: 30, height: 30, borderRadius: '8px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: hot ? '#fdf2d9' : '#f1f4f7',
        }}>
          <Ico sx={{ fontSize: 17, color: hot ? '#c8891a' : '#8a97a5' }} />
        </Box>
      )}
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: hot ? 600 : 400 }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton width={28} height={24} />
      ) : (
        <Chip size="small" color={hot ? 'warning' : 'default'}
          variant={hot ? 'filled' : 'outlined'} label={count} sx={{ minWidth: 34 }} />
      )}
      <ChevronRightIcon className="attn-chevron" sx={{
        fontSize: 18, color: 'text.disabled', flexShrink: 0,
        opacity: 0, transform: 'translateX(-4px)', transition: 'opacity 120ms, transform 120ms',
      }} />
    </Box>
  );
}

function QuickTile({ to, label, ic: Ico }: { to: string; label: string; ic: SvgIconComponent }) {
  return (
    <Box
      component={RouterLink}
      to={`${APP_BASE}${to}`}
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
        py: 2, px: 1.5, borderRadius: 2, border: 1, borderColor: 'divider',
        textDecoration: 'none', color: 'inherit', textAlign: 'center', height: '100%',
        transition: 'background 130ms, border-color 130ms, transform 130ms',
        '&:hover': { bgcolor: 'primary.light', borderColor: 'primary.main', transform: 'translateY(-2px)' },
      }}
    >
      <Box sx={{
        width: 44, height: 44, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#e8f4fd',
      }}>
        <Ico sx={{ fontSize: 22, color: 'primary.main' }} />
      </Box>
      <Box sx={{ fontSize: 12.5, fontWeight: 600 }}>{label}</Box>
    </Box>
  );
}
